/* Copyright 2020-2022 The MathWorks, Inc. */
define(['mw-widget-api/WidgetBase',
    'mw-widget-api/defineWidget',
    'mw-widget-api/facade/render',
    'mw-widget-api/facade/html',
    'mw-widget-api/facade/svg',
    'dojo/i18n!./nls/main'
], function (WidgetBase, defineWidget, render, html, svg, i18n) {
    'use strict';

    const LOCALIZED_STRINGS = {
        stars: [i18n.star0, i18n.star1, i18n.star2, i18n.star3, i18n.star4, i18n.star5],
        rating: i18n.rating
    };

    const RATING_NAME_PREFIX = 'mw_rating_';
    const RATING_HOVER_OVERRIDE_CLASS = 'mwRatingHoverOverride';

    const STAR_SVG_ID = `${RATING_NAME_PREFIX}staricon`;
    const STAR_SVG = svg`<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="16" height="16" viewBox="0 0 16 16">
    <symbol id="${STAR_SVG_ID}"  viewBox="-1 -1 18 18">
    <path d="M5 6h-5l4 4-1 6 5-4 5 4-1-6 4-4h-5l-3-6z" />
    </symbol>
    </svg>`;

    let nameCounter = 0;
    function generateName () {
        // Return a string, that will be unique.
        nameCounter += 1;
        return `${RATING_NAME_PREFIX}${nameCounter}`;
    }

    function booleanToString (bool) {
        return bool ? 'true' : 'false';
    }

    function noFocusChange (widget, evt) {
        if (!widget.contains(evt.relatedTarget)) {
            return true;
        }
    }

    function isRealClickEvent (evt) {
        // For legacy reasons, radio buttons emit
        // click events when changes happen
        // even if the change was due
        // to a keyboard event.
        // This event is hard to differentiate.
        // But there are some differences.
        // Mainly all the major coords will be 0
        // - this could potentially happen if the click
        // - happens on a page that is in the upper
        // - left corner in a doc pinned to the upper
        // - left corner of the screen.
        if (evt.type === 'click') {
            if (evt.clientX === 0 &&
                evt.clientY === 0 &&
                evt.pageX === 0 &&
                evt.pageY === 0 &&
                evt.screenX === 0 &&
                evt.screenY === 0) {
                return false;
            }
            return true;
        }
        return false;
    }

    class Rating extends WidgetBase {
        static get properties () {
            return {
                value: {
                    reflect: true,
                    type: Number
                },
                doesDisplayRating: {
                    reflect: true,
                    type: Boolean
                },
                readOnly: {
                    reflect: true,
                    type: Boolean
                },
                disabled: {
                    reflect: true,
                    type: Boolean
                },
                minimumRating: {
                    reflect: true,
                    type: Boolean
                },
                name: {
                    reflect: true,
                    type: String
                },
                dataTestId: {
                    reflect: true,
                    type: String,
                    attribute: 'data-test-id'
                }
            };
        }

        /**
         * Creates a mw-rating widget
         * @param options {Object} - Initial Object State
         * @param options.value {Integer|String} - Set star rating of widget - 1-5 inclusive and '' for no rating.
         * @param options.doesDisplayRating {Boolean} - Widget displays a rating number text as well as the stars
         * @param options.readOnly {Boolean} - Rating widget is focusable, but does not allow value changes
         * @param options.disabled {Boolean} - Rating widget is dimmed and not focusable.
         * @param options.minimumRating {Boolean} - When set you cannot clear the rating back to no stars interactively
         * @param options.name {String} - User specified string that participates in HTML form POSTs
         */
        constructor () {
            super();
            this._name = generateName();
            this.value = '';
            this._mouseHoveredOnRating = false;
            this.readOnly = false;
            this.disabled = false;
            this.doesDisplayRating = false;
            this.minimumRating = false;
        }

        /**
         * g2837904, Set/Get readonly of the widget.
         * We provide readOnly property, which is the same as readonly,
         * only that the camelCase readOnly confirms with naming guidelines.
         * For backward compatibility, we provide readOnly alongside to readonly.
         */
        get readonly () {
            return this.readOnly;
        }

        set readonly (value) {
            this.readOnly = value;
        }

        /**
         * Set/Get star rating value of widget -  1-5 inclusive and '' for no rating.
         * @type {String|Integer}
         */
        get value () {
            return this._value;
        }

        set value (value) {
            let oldValue = this._value;
            if (value === '' || value < 1) {
                this._value = '';
            } else if (value > 5) {
                this._value = 5;
            } else {
                let tmp = parseInt(value);
                if (isNaN(tmp)) {
                    tmp = '';
                }
                this._value = tmp;
            }
            this.requestUpdate('value', oldValue);
        }

        /**
         * Widget displays a rating number text as well as the stars
         * @member doesDisplayRating {Boolean}
         * @memberof Rating
         * @instance
         */

        /**
         * Rating widget is focusable, but does not allow value changes
         * @member readOnly {Boolean}
         * @memberof Rating
         * @instance
         */

        /**
         * Rating widget is dimmed and not focusable.
         * @member disabled {Boolean}
         * @memberof Rating
         * @instance
         */

        /**
         * When set you cannot clear the rating back to no stars interactively
         * @member minimumRating {Boolean}
         * @memberof Rating
         * @instance
         */

        /**
         * User specified string that participates in HTML form POSTs
         * @member name {String}
         * @memberof Rating
         * @instance
         */

        /**
         * A user specified id used for testing, reflects to 'data-test-id'
         * @member name {String}
         * @memberof Rating
         * @instance
         */

        get _overrideRatingHover () {
            return this.__overrideRatingHover;
        }

        set _overrideRatingHover (value) {
            let oldValue = this.__overrideRatingHover;
            this.__overrideRatingHover = value;
            this.requestUpdate('_overrideRatingHover', oldValue);
        }

        get _noInput () {
            return this.readOnly || this.disabled;
        }

        firstUpdated () {
            this._addSVGTemplate();
            this.setAttribute('role', 'radiogroup');
        }

        updated (changedProperties) {
            if (changedProperties.has('disabled')) {
                if (this.disabled) {
                    // Firefox doesn't clear focus on radio
                    // buttons when they are disabled
                    let focusedElem = document.activeElement;
                    if (this.contains(focusedElem)) {
                        focusedElem.blur();
                    }
                }
            }

            if (changedProperties.has('value')) {
                // Show the new value, rather than a hover
                // preview of what would happen if you click
                this._overrideRatingHover = true;
                this.setAttribute('aria-valuenow', this.value);
            }
            if (changedProperties.has('disabled')) {
                this.setAttribute('aria-disabled', booleanToString(this.disabled));
            }
            if (changedProperties.has('readOnly')) {
                this.setAttribute('aria-readonly', booleanToString(this.readOnly));
            }

            if (this._wasCommit && this._focusedValue !== this.value) {
                // "committed changes" (Focus Loss and Click)
                this.emit('change', {
                    oldValue: this._focusedValue,
                    newValue: this.value
                }, { bubbles: true, cancelable: false }
                );
                this._saveFocusedValue();
            }
            this._wasCommit = false;
        }

        _addSVGTemplate () {
            // This remains in the DOM for the life of the application
            // once added. If needed we could track if "no star ratings"
            // are left, and remove from DOM.  Since this is shared across
            // all instances of the widget, we cannot just remove it on
            // disconnected and add on connected.  We would need to
            // reference count.
            if (!document.getElementById(STAR_SVG_ID)) {
                let div = document.createElement('div');
                div.style.display = 'none';
                document.body.appendChild(div);
                render(STAR_SVG, div);
            }
        }

        _handleRadioButtonChange (evt) {
            let oldValue = this.value;
            this.value = evt.target.value;

            // If  we accept input,
            // this was a click event
            // with the primary mouse button
            if (!this._noInput && isRealClickEvent(evt)) {
                // This is a potential commit if the value changed.
                this._radioCommit();
            } else if (evt.type === 'change' ||
                evt.type === 'input' ||
                evt.type === 'input'
            ) {
                // mw-rating emits its own change and input event,
                // so we want to hide the internal event.

                // For click, if it is a real click we don't eat the event
                // but we eat the fake clicks emitted by arrow keys changing
                // the radio button
                this._eatInternalEvent(evt);
            }

            this.updateComplete.then(() => {
                if (oldValue !== this.value) {
                    // Emit input on all changes - not just
                    // "committed changes (Focus Loss and Click)"
                    this.emit('input', {
                        oldValue: oldValue,
                        newValue: this.value
                    }, { bubbles: true, cancelable: false }
                    );
                }
            });
        }

        _saveFocusedValue () {
            // Saves the value when the user gives the control
            // focus.  Used to deturmine if we should emit a change
            // event on commit (focusout, click)
            this._focusedValue = this.value;
        }

        _handleRadioContainerFocusIn (evt) {
            if (noFocusChange(this, evt)) {
                this._saveFocusedValue();
            }
        }

        _handleRadioContainerFocusOut (evt) {
            if (noFocusChange(this, evt)) {
                // Focus Out may fire on each element in the radio container
                // So only commit if relatedTarget is outside the widget
                this._radioCommit();
            }
        }

        _radioCommit () {
            // Called when a change event needs to be
            // emitted if a value changed due to this
            // action
            this._wasCommit = true;

            // Force a request update to pick up any
            // changes since focusin or click.
            // If there are we want to emit change.
            this.requestUpdate();
        }

        _handleRatingHovered (evt) {
            // We turn off the override if we just moved the mouse, allowing standard hover
            // preview to work.
            // When this is on (like we just changed the value but have not moved the mouse) we
            // want the new value to appear not the hover preview.
            //
            // Important when user has clicked/hovered over the rating, and then uses the
            // keyboard to change values.  We want those new values to be shown, not the hover
            // preview.
            this._overrideRatingHover = false;
        }

        _eatInternalEvent (evt) {
            // RadioButton emits a click event, even when using keyboard.
            // Prevent this event

            // RadioButton emits a change event, we don't want downstream
            // seeing that because we emit our own.

            // We eat both events by calling stopPropagation on them.
            // ShadowDOM would do this automatically for us.
            evt.stopPropagation();
        }

        _assignChildFocus () {
            let checkedButton = this.querySelector('input:checked');
            if (checkedButton) {
                checkedButton.focus();
            }
        }

        _ratingButtonRenderer (ratingConfig, number) {
            let id = `${ratingConfig.name}_${number}`;
            let radioValue = number;
            let starSvgRef = svg`<svg aria-hidden='true' focusable='false'><use href="#${STAR_SVG_ID}"/></svg>`;

            let isDisabled = this._noInput;

            if (number === 0) {
                starSvgRef = html``;
                radioValue = '';
                if (this.minimumRating && this.value !== '') {
                    // Don't allow selection of radio button 0, if not checked
                    // It if is checked allow it to be changed into a non-empty
                    // value
                    isDisabled = true;
                }
            }

            let isChecked = radioValue === this.value;
            if (isChecked && this.readOnly) {
                // Allow only selected radio button
                // to have focus when readOnly
                isDisabled = false;
            }

            // Label has a 'tab-index because otherwise focus-within was 'losing focus'
            // When we clicked on the star.
            // See: https://stackoverflow.com/questions/49662769/focus-within-styles-flash-when-clicking-an-input-label

            // Also special case disabled alone, we don't
            // want label to be focusable even with a click
            // if the whole widget is disabled.
            let tabindexAttr = this.disabled ? '' : '-1';
            return html`<input 
                type='radio'  
                class='mwRatingInput' 
                name='${ratingConfig.name}' 
                id='${id}' 
                value='${radioValue}'
                .disabled=${isDisabled}
                .checked=${isChecked}
                @input=${this._noInput ? null : this._handleRadioButtonChange}
                @change=${this._noInput ? null : this._handleRadioButtonChange}
                @click=${this._noInput ? null : this._handleRadioButtonChange}
            /><label tabindex='${tabindexAttr}' for='${id}' class='mwRatingLabel'>
             ${starSvgRef}<span>${LOCALIZED_STRINGS.stars[number]}</span>
             </label>`;
        }

        _ratingValueRenderer (number) {
            if (number > 0) {
                return html`<span @click=${this.focus} aria-label="${LOCALIZED_STRINGS.rating}" class='mwRatingDisplay'>(${number})</span>`;
            }
            return null;
        }

        /**
         * Sets focus to the widget
         */
        focus () {
            if (!this.disabled) {
                this._assignChildFocus();
            }
        }

        render () {
            let doesDisplayRating = null;
            let hoverOverrideClass = '';
            if (this.doesDisplayRating) {
                doesDisplayRating = this._ratingValueRenderer(this.value);
            }
            if (this._overrideRatingHover || this._noInput) {
                hoverOverrideClass = RATING_HOVER_OVERRIDE_CLASS;
            }

            // Use this.name if valid and specified otherwise use our default
            // Usually only set if we need to participate in form submission
            if (this._name !== this.name && this.name && this.name.trim() !== '') {
                this._name = this.name.trim();
            }

            let ratingConfig = { name: this._name };
            let ratings = [
                this._ratingButtonRenderer(ratingConfig, 0),
                this._ratingButtonRenderer(ratingConfig, 1),
                this._ratingButtonRenderer(ratingConfig, 2),
                this._ratingButtonRenderer(ratingConfig, 3),
                this._ratingButtonRenderer(ratingConfig, 4),
                this._ratingButtonRenderer(ratingConfig, 5)
            ];

            return html`<div class='mwRatingLayout'><div
                @mousemove=${this._noInput ? null : this._handleRatingHovered}
                @focusin=${this._handleRadioContainerFocusIn}
                @focusout=${this._noInput ? null : this._handleRadioContainerFocusOut}
                class='mwRatingContainer ${hoverOverrideClass}'
                @click=${this._eatInternalEvent}
                @input=${this._eatInternalEvent}
                @change=${this._eatInternalEvent}
                >${ratings}</div
                >${doesDisplayRating}</div>`;
        }
    }
    return defineWidget({
        name: 'mw-rating',
        widgetClass: Rating
    });
});
